import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { timeAgo, sentimentColor, severityColor } from "../lib/format";
import { Waveform, TrendUp, CaretRight } from "@phosphor-icons/react";

export default function SideComplaintsPanel() {
  const [complaints, setComplaints] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [tab, setTab] = useState("live");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [c, t] = await Promise.all([api.latestComplaints(20), api.topProducts(6)]);
        if (!cancelled) {
          setComplaints(c);
          setTopProducts(t);
        }
      } catch (e) {
        // silent
      }
    };
    load();
    const id = setInterval(load, 25000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <aside
      data-testid="side-panel"
      className="hidden lg:flex flex-col w-[360px] xl:w-[400px] shrink-0 bg-[#F7F5F0] border-l border-[#E5E2DC] sticky top-[73px] h-[calc(100vh-73px)]"
    >
      <div className="flex border-b border-[#E5E2DC] bg-white">
        <button
          type="button"
          data-testid="side-tab-live"
          onClick={() => setTab("live")}
          className={`flex-1 px-4 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold flex items-center justify-center gap-2 transition-colors ${
            tab === "live"
              ? "text-[#1C1B1A] border-b-2 border-[#A66E4E]"
              : "text-[#6E6B65] border-b-2 border-transparent hover:text-[#1C1B1A]"
          }`}
        >
          <Waveform size={14} weight="bold" />
          Live Feed
        </button>
        <button
          type="button"
          data-testid="side-tab-top"
          onClick={() => setTab("top")}
          className={`flex-1 px-4 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold flex items-center justify-center gap-2 transition-colors ${
            tab === "top"
              ? "text-[#1C1B1A] border-b-2 border-[#A66E4E]"
              : "text-[#6E6B65] border-b-2 border-transparent hover:text-[#1C1B1A]"
          }`}
        >
          <TrendUp size={14} weight="bold" />
          Top Ranked
        </button>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll">
        <AnimatePresence mode="wait">
          {tab === "live" ? (
            <motion.div
              key="live"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="divide-y divide-[#E5E2DC]"
              data-testid="complaints-feed"
            >
              {complaints.length === 0 && (
                <div className="p-6 text-sm text-[#6E6B65]">No incoming complaints.</div>
              )}
              {complaints.map((c, idx) => {
                const sev = severityColor(c.severity);
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.02 }}
                    data-testid="complaint-item"
                    className="p-4 hover:bg-[#EFECE6]/40 transition-colors cursor-default"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ background: sev.fg }}
                          aria-hidden
                        />
                        <span className="eyebrow text-[9px]">{c.source} · {c.region}</span>
                      </div>
                      <span className="text-[10px] text-[#6E6B65] font-mono">{timeAgo(c.created_at)}</span>
                    </div>
                    <Link
                      to={`/product/${c.product_id}`}
                      className="block font-heading text-[15px] leading-tight mb-1 hover:text-[#A66E4E] transition-colors"
                    >
                      {c.brand} — {c.product_name}
                    </Link>
                    <p className="text-[13px] text-[#1C1B1A]/80 leading-snug line-clamp-2">
                      {c.text}
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                      <span
                        className="text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5"
                        style={{ background: sev.bg, color: sev.fg }}
                      >
                        {c.severity}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-[#6E6B65]">
                        {c.category.replace("_", " ")}
                      </span>
                      <span
                        className="ml-auto text-[10px] font-mono"
                        style={{ color: sentimentColor(c.sentiment) }}
                      >
                        {c.sentiment_score.toFixed(2)}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="top"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="divide-y divide-[#E5E2DC]"
              data-testid="top-products-list"
            >
              {topProducts.map((p, i) => (
                <Link
                  key={p.product_id}
                  to={`/product/${p.product_id}`}
                  data-testid="top-product-item"
                  className="flex items-center gap-3 p-4 hover:bg-[#EFECE6]/50 transition-colors"
                >
                  <span className="font-heading text-2xl font-light w-7 text-[#A66E4E] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow text-[9px]">{p.brand}</div>
                    <div className="font-accent text-[17px] leading-tight truncate italic">
                      {p.product_name}
                    </div>
                    <div className="text-[11px] text-[#6E6B65] mt-0.5">
                      {p.volume} signals · sentiment {p.avg_sentiment.toFixed(2)}
                    </div>
                  </div>
                  <CaretRight size={14} className="text-[#A66E4E]" />
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="px-4 py-3 border-t border-[#E5E2DC] bg-white">
        <div className="flex items-center justify-between text-[10px] tracking-[0.18em] uppercase text-[#6E6B65]">
          <div className="flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[#40593B] vigil-ping" />
              <span className="relative rounded-full w-2 h-2 bg-[#40593B]" />
            </span>
            Live
          </div>
          <span className="font-mono">updates every 25s</span>
        </div>
      </div>
    </aside>
  );
}
